import Clarity from '@microsoft/clarity'

const projectId = process.env.VUE_APP_CLARITY_PROJECT_ID

// Route params kept as-is in the page name; others (ids) stay as placeholders to keep pages groupable
const MEANINGFUL_PARAMS = ['tab', 'mode']

// Clarity's helpers call window.clarity directly and throw when init was skipped (no project id)
const enabled = () => !!projectId && typeof window.clarity === 'function'

const CONSENT_KEY = 'clarityConsent'
// TEMPORARY: grants consent to users with no stored CMP choice (logged in before the CMP wiring); set to false to stop
const FORCE_CONSENT_WITHOUT_CHOICE = true

let currentUser = null
let currentPage = null

export function initClarity() {
  if (!projectId) return
  Clarity.init(projectId)
  // The CMP popup only runs on Auth.vue: the choice it stored there is replayed on every page load
  const stored = localStorage.getItem(CONSENT_KEY)
  if (stored) applyConsent(stored === 'granted')
  else if (FORCE_CONSENT_WITHOUT_CHOICE) applyConsent(true)
}

// Without a consentV2 signal Clarity runs cookieless for EEA visitors and splits recordings per page load
export function listenCmpConsent() {
  if (typeof window.__tcfapi !== 'function') return
  window.__tcfapi('addEventListener', 2, (tcData, success) => {
    if (!success || !['tcloaded', 'useractioncomplete'].includes(tcData.eventStatus)) return
    // TCF purposes 1 (store/access device info) + 8 (measure content performance)
    const granted = !tcData.gdprApplies || (!!tcData.purpose.consents[1] && !!tcData.purpose.consents[8])
    localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied')
    applyConsent(granted)
  })
}

function applyConsent(granted) {
  if (!enabled()) return
  Clarity.consentV2({ ad_Storage: 'denied', analytics_Storage: granted ? 'granted' : 'denied' })
}

export function identifyClarityUser(user) {
  if (!enabled() || !user) return
  currentUser = user
  identify()
  Clarity.setTag('user_id', String(user.id))
  if (user.email) Clarity.setTag('email', user.email)
  if (user.role && user.role.length) Clarity.setTag('role', user.role[0])
  if (user.admin) Clarity.setTag('admin', 'true')
  // Users still on the installed PWA (being retired): filter on this tag to email them about the web/mobile apps
  if (isInstalledPwa()) {
    Clarity.setTag('display_mode', 'pwa')
    Clarity.event('pwa_session')
  }
}

// Clarity strips location.hash before detecting navigations, so hash-mode routes never show up as pages:
// each route change is pushed as an event + tag + custom page id instead
export function trackClarityPage(route) {
  if (!enabled()) return
  const page = clarityPageName(route)
  if (page === currentPage) return
  currentPage = page
  Clarity.setTag('page', page)
  Clarity.event(`page:${page}`)
  identify()
}

export function clarityEvent(name) {
  if (enabled()) Clarity.event(name)
}

function identify() {
  if (!currentUser) return
  const email = currentUser.email || undefined
  Clarity.identify(email || String(currentUser.id), undefined, currentPage || undefined, email)
}

function clarityPageName(route) {
  const record = route.matched[route.matched.length - 1]
  if (!record) return route.path
  return record.path.replace(/:(\w+)\??/g, (placeholder, param) =>
    MEANINGFUL_PARAMS.includes(param) && route.params[param] ? route.params[param] : `:${param}`
  ).replace(/\/:(tab|mode)$/, '')
}

function isInstalledPwa() {
  return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true
}
